import {
	Container,
	ContainerGutter,
	ContainerPadding,
	Image,
	TextTag,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { useOfertaCard } from '../../../context/ofertas-card-context';
import { OfertasContext } from '../../../context/ofertas-context';
import { getEtiquetaDgt } from '../../../helpers/ofertas-helper';
import { useOfertasTracker } from '../../../helpers/use-ofertas-tracker';
import { CarlineWithOfertas, Oferta } from '../../../services/ofertas-service/models/ofertas';
import { RedirectCTAs } from '../../redirect-cta';
import { OpenLayerCTA } from '../oferta-cta';
import { OfertaPrice } from '../oferta-price';
import { OfertaPromotionLabel } from '../oferta-promotion-label';
import { OfertaCardDescriptionDesktop } from './oferta-card-description-desktop';
import {
	AnimatedCardWrapper,
	CardControllers,
	CollapsibleSection,
	DGTStickerContainer,
	OpenLayerCTAContainer,
	StyledText,
	StyledTextSingleLine,
	TwoColumnsDiv,
} from './ui/oferta-card-desktop-styles';

export const OfertaCardDesktopAnimated = ({
	carline,
	className,
	currentOferta,
	ofertasNumber,
}: {
	className?: string;
	carline: CarlineWithOfertas;
	currentOferta: Oferta;
	ofertasNumber: number;
}): React.ReactElement => {
	const { serviceConfigProvider } = React.useContext(OfertasContext);
	const { selectedOferta, isCallMeNowOpen, setSelectedOferta, handleLayerOnClick, setSelectedPromotion } =
		useOfertaCard();

	const { trackOfertasButtonClick } = useOfertasTracker();

	const isActive = selectedOferta?.id === currentOferta.id;

	return (
		<AnimatedCardWrapper
			className={className}
			ofertasNumber={ofertasNumber}
			isActive={isActive}
			onClick={() => {
				if (!isActive) {
					setSelectedOferta(currentOferta);
					trackOfertasButtonClick('Show Oferta Info', currentOferta, carline);
				}
			}}
		>
			<Container
				data-testid="oferta-card-info-container-desktop"
				wrap={'always'}
				verticalAlign={'center'}
				horizontalAlign={'space-between'}
				stretchContent
			>
				<Container wrap={'always'} stretchContent>
					{/* (Promociones) + Título + sticker dgt + motorización */}
					<TwoColumnsDiv isActive={isActive} ofertasNumber={ofertasNumber}>
						<div>
							{/* 1. SECCIÓN COLLAPSIBLE: Promociones */}
							<CollapsibleSection isOpen={isActive && !!currentOferta.promociones}>
								<div>
									{currentOferta.promociones && (
										<OfertaPromotionLabel
											handleLayerOnClick={handleLayerOnClick}
											promociones={currentOferta.promociones}
											setSelectedPromotion={setSelectedPromotion}
										/>
									)}
								</div>
							</CollapsibleSection>

							<Container wrap={'always'} gutter={ContainerGutter.static100} verticalAlign={'center'}>
								<Container wrap="auto" gutter={ContainerGutter.static200} verticalAlign={'center'}>
									<StyledText
										tag={TextTag.h3}
										appearance={isActive ? TokenTextAppearance.headline400 : TokenTextAppearance.headline300}
										bold
										wordBreak
									>
										{carline.nuevo ? 'Nuevo ' : ''}
										{currentOferta.carline?.salesgroup?.title}
									</StyledText>

									<DGTStickerContainer isActive={isActive}>
										<Image
											src={
												currentOferta.configuracion.etiquetaDGT &&
												getEtiquetaDgt(currentOferta.configuracion.etiquetaDGT)
											}
											alt={carline.title}
											width="100%"
											height="100%"
										/>
									</DGTStickerContainer>
								</Container>

								{/* Añadimos el nombre del motor siempre debajo del título para simplificar la animación */}
								<div style={{ display: 'grid' }}>
									<StyledTextSingleLine
										appearance={isActive ? TokenTextAppearance.label200 : TokenTextAppearance.label150}
										wordBreak
									>
										{currentOferta.configuracion.motorizacion.name}
									</StyledTextSingleLine>
								</div>
							</Container>
						</div>

						<CardControllers>
							<OpenLayerCTAContainer isActive={isActive}>
								<OpenLayerCTA buttonSize="large" carline={carline} />
							</OpenLayerCTAContainer>
						</CardControllers>
					</TwoColumnsDiv>

					{/* 2. SECCIÓN COLLAPSIBLE: Descripción */}
					<CollapsibleSection isOpen={isActive && (!!currentOferta.titulo || !!currentOferta.descripcion)}>
						<div>
							<OfertaCardDescriptionDesktop className="oferta-card-description" oferta={currentOferta} />
						</div>
					</CollapsibleSection>

					<OfertaPrice oferta={currentOferta} brand={carline.brand} isActive={isActive} />
				</Container>

				{/* 3. SECCIÓN COLLAPSIBLE: CTAs */}
				<CollapsibleSection isOpen={isActive}>
					<div>
						<Container
							padding={{ top: ContainerPadding.static200 }}
							gutter={
								(
									serviceConfigProvider.configs['es-vwdata']?.customConfig as {
										brand?: string;
									}
								)?.brand == 'N'
									? ContainerGutter.dynamic0050
									: ContainerGutter.static0
							}
							verticalAlign="center"
							wrap={'never'}
						>
							<RedirectCTAs
								copy={selectedOferta?.calculatedFinance.rawResponse ? 'Pide presupuesto' : 'Pide esta oferta'}
								carline={carline}
								emphasis="primary"
								oferta={currentOferta}
								formTrigger={'es-oneform-v2'}
								isMobile={false}
							/>

							{(
								serviceConfigProvider.configs['es-vwdata']?.customConfig as {
									brand?: string;
								}
							)?.brand == 'N' &&
								isCallMeNowOpen && (
									<RedirectCTAs
										copy={'¿Te llamamos?'}
										carline={carline}
										emphasis="secondary"
										oferta={currentOferta}
										formTrigger="es-oneform-callme-embedded"
										isMobile={false}
									/>
								)}
						</Container>
					</div>
				</CollapsibleSection>
			</Container>
		</AnimatedCardWrapper>
	);
};
